import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { API_CONFIG } from '../config/api';
import { ProfileImageResponse } from '../types';

export const useProfileImage = (rut: string) => {
  // Simple inline SVG placeholder (used when backend returns 404 / no image)
  const DEFAULT_PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='128' height='128'><rect width='100%25' height='100%25' fill='%23e5e7eb'/><circle cx='64' cy='44' r='30' fill='%239ca3af'/><rect x='20' y='88' width='88' height='20' rx='10' fill='%239ca3af'/></svg>";

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Query para obtener la imagen de perfil
  const { data: profileImageData, isLoading: isLoadingImage, refetch: refetchImage } = useQuery<ProfileImageResponse | null>({
    queryKey: ['profile-image', rut],
    queryFn: async (): Promise<ProfileImageResponse | null> => {
      if (!rut) return null;
      try {
        // Intentar endpoint /profile-photos/:rut/image que puede devolver metadata
        try {
          const res = await apiService.getProfilePhoto(rut);
          // Si backend respondió con data válida, úsala
          if (res && res.success && res.data && res.data.profile_image_url) {
            return res as ProfileImageResponse;
          }
        } catch (err: any) {
          // Si el endpoint no existe o devuelve 404, lo manejamos abajo
          if (!(err.response?.status === 404 || err.name === 'SilentError')) {
            // Propagar otros errores para que react-query los maneje
            throw err;
          }
        }

        // Si no obtuvimos url desde profile-photos, obtener el registro de personal
        try {
          const personResp: any = await apiService.getPersonalByRut(rut);
          const person = personResp?.data ?? personResp;
          if (person && person.profile_image_url) {
            return { success: true, data: { profile_image_url: person.profile_image_url, rut } } as ProfileImageResponse;
          }
        } catch (err: any) {
          // Si tampoco funciona, no fallar; retornamos null para usar placeholder
          if (!(err.response?.status === 404 || err.name === 'SilentError')) {
            console.warn('⚠️ No se pudo obtener profile_image_url desde personal:', err?.message || err);
          }
        }

        // No hay imagen conocida
        return null;
      } catch (err: any) {
        if (err.response?.status === 404 || err.name === 'SilentError') {
          return null;
        }
        throw err;
      }
    },
    enabled: !!rut,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404 || error?.name === 'SilentError' || failureCount >= 1) {
        return false;
      }
      return true;
    },
    retryDelay: 1000,
    onError: (error: any) => {
      if (error.response?.status !== 404 && error.name !== 'SilentError') {
        console.error('❌ Error al obtener imagen de perfil:', error);
      }
    }
  });

  // Mutation para subir imagen
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      // Intentar endpoint alternativo /profile-photos primero
      try {
        return await apiService.uploadProfilePhoto(rut, file);
      } catch (err: any) {
        // Fallback a /personal/:rut/upload
        return await apiService.uploadProfileImage(rut, file);
      }
    },
    onSuccess: (result: any) => {
      // Actualizar la imagen local inmediatamente
      if (result.data?.profile_image_url) {
        setProfileImage(result.data.profile_image_url);
      }
      // Invalidar la query para refrescar datos
      queryClient.invalidateQueries({ queryKey: ['profile-image', rut] });
      try {
        const ev = new CustomEvent('personalUpdated', { detail: { id: rut, rut, nombre: null, updated: result } });
        window.dispatchEvent(ev);
      } catch (err) {
        // ignore
      }
    },
    onError: (error: any) => {
      console.error('❌ Error al subir imagen:', error);
      setError(error.message || 'Error al subir imagen');
    }
  });

  // Mutation para eliminar imagen
  const deleteImageMutation = useMutation({
    mutationFn: async () => {
      // Try to delete from both endpoints, but don't fail the whole operation if
      // the image is already gone. In that case we try to clear the DB reference
      // so UI doesn't keep offering delete for a non-existing image.
      try {
        try {
          await apiService.deleteProfilePhoto(rut);
          return { success: true, data: null } as any;
        } catch (err: any) {
          if (!(err.response?.status === 404 || err.name === 'SilentError')) {
            throw err;
          }
          // else continue to try other deletion methods
        }

        try {
          await apiService.deleteProfileImage(rut);
          return { success: true, data: null } as any;
        } catch (err: any) {
          if (!(err.response?.status === 404 || err.name === 'SilentError')) {
            throw err;
          }
          // Image not found on server endpoints. Attempt to clear DB reference.
          try {
            const personResp: any = await apiService.getPersonalByRut(rut);
            const person = personResp?.data ?? personResp;
            const id = person?.id || person?.data?.id || person?.personal?.id || null;
            if (id) {
              try {
                await apiService.updatePersonal(id, { profile_image_url: null } as any);
                return { success: true, data: null } as any;
              } catch (uerr) {
                console.warn('⚠️ No se pudo limpiar profile_image_url vía updatePersonal:', uerr);
              }
            }
            // Fallback: attempt update by rut (may be rejected by backend validation)
            try {
              await apiService.updatePersonalData(rut, { profile_image_url: null } as any);
              return { success: true, data: null } as any;
            } catch (uerr) {
              console.warn('⚠️ No se pudo limpiar profile_image_url vía updatePersonalData:', uerr);
            }
          } catch (fetchErr) {
            console.warn('⚠️ No se pudo obtener personal para limpiar profile_image_url:', fetchErr);
          }

          // If all attempts failed, resolve gracefully (don't throw) so UI can clear local state
          return { success: false, data: null } as any;
        }
      } catch (error) {
        // Propagar errores reales (not found is handled above)
        throw error;
      }
    },
    onSuccess: () => {
      setProfileImage(null);
      // Evitar refetch inmediato que vuelve a lanzar GET 404 al reabrir modal:
      // en su lugar, actualizar el cache a `null` para indicar que no hay imagen.
      try {
        queryClient.setQueryData(['profile-image', rut], null);
      } catch (e) {
        // Fallback a invalidar si setQueryData falla
        queryClient.invalidateQueries({ queryKey: ['profile-image', rut] });
      }
      try {
        const ev = new CustomEvent('personalUpdated', { detail: { id: rut, rut, nombre: null, updated: null } });
        window.dispatchEvent(ev);
      } catch (err) {
        // ignore
      }
    },
    onError: (error: any) => {
      // No tratar 404 / SilentError como errores visibles para el usuario.
      if (error?.name === 'SilentError' || error?.response?.status === 404) {
        console.warn('⚠️ Imagen ya no existe en servidor:', error?.message || error);
        setProfileImage(DEFAULT_PLACEHOLDER);
        return;
      }
      console.error('❌ Error al eliminar imagen:', error);
      setError(error.message || 'Error al eliminar imagen');
    }
  });

  // Actualizar la imagen local cuando cambien los datos
  useEffect(() => {
    if (profileImageData?.data?.profile_image_url) {
      let url = profileImageData.data.profile_image_url;

      // Normalizar URL:
      // - Si es Google Drive, convertir a enlace directo embebible
      // - Si es absoluta (http/https) dejarla tal cual
      // - Si es relativa (p.e. "/uploads/.."), construir URL absoluta usando
      //   primero API_CONFIG.BASE_URL (sin '/api') y luego el respaldo FALLBACK_BASE_URL
      try {
        const isAbsolute = /^https?:\/\//i.test(url);

        // Google Drive share links -> convertir a enlace directo
        if (/https?:\/\/((drive|docs)\.google\.com)/i.test(url)) {
          // Intentar extraer id: /file/d/:id/ o ?id=...
          const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
          let driveId: string | null = null;
          if (m && m[1]) driveId = m[1];
          if (!driveId) {
            const q = url.split('?')[1] || '';
            const params = new URLSearchParams(q);
            const idParam = params.get('id');
            if (idParam) driveId = idParam;
          }
          if (driveId) {
            url = `https://drive.google.com/uc?export=view&id=${driveId}`;
          }
        }

        if (!isAbsolute) {
          // Primero la API configurada y solo después el respaldo fijo a Cloud Run: al revés,
          // cualquier entorno apuntado a otro backend igual pedía las fotos a producción.
          const candidates = [API_CONFIG.BASE_URL, API_CONFIG.FALLBACK_BASE_URL];
          let built = null;
          for (const c of candidates) {
            if (!c) continue;
            try {
              const base = c.replace(/\/api\/?$/i, '');
              built = `${base}${url.startsWith('/') ? '' : '/'}${url}`;
              // No hacemos comprobación de reachability aquí; usamos el primero válido
              break;
            } catch (e) {
              // ignore and try next
            }
          }
          if (built) url = built;
        }
      } catch (e) {
        console.warn('⚠️ Error normalizando profile_image_url:', e);
      }

      setProfileImage(url);
    } else {
      // Usar placeholder inline cuando no hay imagen (evita muchos 404s visibles)
      setProfileImage(DEFAULT_PLACEHOLDER);
    }
  }, [profileImageData]);

  // Función para subir imagen con validación
  const uploadImage = async (file: File) => {
    setError(null);
    
    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = 'Solo se permiten imágenes: JPG, PNG, GIF, WEBP';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      const errorMsg = 'La imagen no puede ser mayor a 5MB';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const result = await uploadImageMutation.mutateAsync(file);
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Función para eliminar imagen
  const deleteImage = async () => {
    setError(null);
    try {
      await deleteImageMutation.mutateAsync();
    } catch (error) {
      throw error;
    }
  };

  // Función para refrescar datos
  const refetch = () => {
    refetchImage();
  };

  const hasImage = !!(profileImageData && profileImageData.data && profileImageData.data.profile_image_url);

  return {
    profileImage,
    hasImage,
    loading: isLoadingImage || uploadImageMutation.isLoading || deleteImageMutation.isLoading,
    error,
    uploadImage,
    deleteImage,
    refetch,
    isUploading: uploadImageMutation.isLoading,
    isDeleting: deleteImageMutation.isLoading
  };
};

// Función utilitaria para validar archivos de imagen
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Solo se permiten imágenes: JPG, PNG, GIF, WEBP'
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'La imagen no puede ser mayor a 5MB'
    };
  }

  return { isValid: true };
};

// Función utilitaria para formatear tamaño de archivo
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
